import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { type Ref } from "react";

import { ConfirmationDialog, Icon, SkiliticsIcon, Tooltip } from "@skilitics/designer-ui-components";
import type { InteractModuleListItem } from "@skilitics/nautilus-endpoint-definition";

import { UserAvatar } from "../../../../ui/UserAvatar.tsx";
import { DuplicateModuleWizard } from "../../../wizards";
import { ModuleMenu } from "../ModuleMenu";
import { useModuleItem, type Status } from "../shared.ts";
import { ActionButtons, ActionButtonsContainer } from "./ActionButtons";
import {
  CardContainer,
  DetailsContainer,
  GradientOverlay,
  IconContainer,
  IconWrapper,
  Thumbnail,
  Title,
} from "./GridCardElements";

const sharedCss = css`
  transform: scale(1.05);

  ${GradientOverlay} {
    transform: translateY(25px);
  }

  ${ActionButtonsContainer} {
    pointer-events: auto;
    opacity: 1;
  }

  &:hover ${ActionButtonsContainer} {
    transition: opacity 125ms ease-in-out;
  }
`;

const Container = styled(CardContainer)<{ $busy: Status; $hovered: boolean }>`
  transition: ${({ $busy }) =>
    $busy !== "idle" ? "transform 250ms ease-in-out 250ms" : "transform 250ms ease-in-out"};

  ${({ $hovered }) => $hovered && sharedCss};

  ${ActionButtonsContainer} {
    transition: opacity 250ms ease-in-out;
  }

  &:hover {
    ${sharedCss}

    transition: transform 125ms ease-in-out;
  }

  &.fade-enter {
  }

  &.fade-enter-active {
  }
`;

type Props = {
  module: InteractModuleListItem;
  title: string;
  ref?: Ref<HTMLDivElement>;
};

const StyledAvatar = styled(UserAvatar)`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.primary};
`;

export const GridItem = ({ module, title: moduleTitle, ref }: Props) => {
  const {
    formatMessage,
    moduleUrl,
    url,
    busy,
    setBusy,
    hovered,
    alreadyOpenMessage,
    handleMenuOpen,
    handleMenuClose,
    handleMenuSelection,
    deleteDialogProps,
    duplicateWizardProps,
  } = useModuleItem(module);

  return (
    <Tooltip
      enterDelay={500}
      placement="top"
      title={
        <>
          <div>{moduleTitle}</div>
          <div>{module.learningObjective}</div>
        </>
      }
    >
      <Container
        ref={ref}
        $busy={busy}
        $hovered={hovered}
        onDoubleClick={(event) => {
          if (!event.currentTarget.contains(event.target as Node)) return;
          window.location.href = moduleUrl;
        }}
        data-test="grid-module-card-container"
      >
        <Thumbnail>
          <img src={url} alt={formatMessage("thumbnail")} />
        </Thumbnail>
        <GradientOverlay />
        <DetailsContainer>
          <Title>{moduleTitle}</Title>

          <ModuleMenu
            module={module}
            busy={busy}
            setBusy={setBusy}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuSelection={handleMenuSelection}
          />
        </DetailsContainer>
        <IconContainer>
          {module.alreadyOpenUser && (
            <Tooltip title={alreadyOpenMessage} placement="top">
              <StyledAvatar email={module.alreadyOpenUser.email} />
            </Tooltip>
          )}
          <IconWrapper $show={!!module.readonly} $busy={busy === "deleting"}>
            <Icon icon="trash-alt" size={15} />
          </IconWrapper>
          <IconWrapper $show={!!module.readonly} $busy={busy === "locking"}>
            <SkiliticsIcon icon="lock" size={15} />
          </IconWrapper>
        </IconContainer>
        <ActionButtons moduleId={module.id} disabled={busy !== "idle"} setBusy={setBusy} />
        <ConfirmationDialog {...deleteDialogProps} />
        <DuplicateModuleWizard {...duplicateWizardProps} />
      </Container>
    </Tooltip>
  );
};
